// Utility helpers for uploading email attachments to Azure Blob Storage
// and returning their public URLs so they can be embedded in emails.
//
// NOTE: These helpers use a SAS token that must be kept secure and rotated
// periodically. If you rotate the SAS in Azure, update the `token` value
// below (without the leading '?').

// Azure Storage account name used for report/uploads.
let accountName = "awakenstorage";

export const getAccountName = () => {
  return Promise.resolve(accountName);
};

// Azure Blob container name where attachments will be uploaded.
let container = "uploads";

export const getContainer = () => {
  return Promise.resolve(container);
};

// SAS token for the container or account.
// IMPORTANT: This should be rotated periodically in Azure.
let token =
  "sp=racwdl&st=2025-12-19T07:21:48Z&se=2026-12-19T15:36:48Z&spr=https&sv=2024-11-04&sr=c&sig=c6fdX1gS0NvKgcuiCAKFq%2FwWphJAr8CLm3ztoniGEUU%3D";

export const getSASToken = () => {
  return Promise.resolve(token);
};

// Optional helper: full blob endpoint with SAS for direct access if needed.
export const getBlobUrl = () => {
  return Promise.all([getAccountName(), getContainer(), getSASToken()]).then(
    ([name, cont, sas]) =>
      `https://${name}.blob.core.windows.net/${cont}?${sas}`
  );
};

// Upload a single file to Azure Blob Storage and return the public URL
// (without the SAS token) that can be shared via email.
export async function uploadFileToAzure(file) {
  if (!file) {
    throw new Error("No file provided for upload");
  }

  const [name, cont, sas] = await Promise.all([
    getAccountName(),
    getContainer(),
    getSASToken(),
  ]);

  // Create a unique blob name using timestamp + original name
  const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
  const safeFileName = file.name.replace(/[^a-zA-Z0-9_.-]/g, "_");
  const blobName = `${timestamp}_${safeFileName}`;

  const uploadUrl = `https://${name}.blob.core.windows.net/${cont}/${blobName}?${sas}`;

  const response = await fetch(uploadUrl, {
    method: "PUT",
    headers: {
      "x-ms-blob-type": "BlockBlob",
      "Content-Type": file.type || "application/octet-stream",
    },
    body: file,
  });

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    console.error("Azure upload failed:", response.status, text);
    throw new Error(
      `Failed to upload attachment to cloud (status ${response.status})`
    );
  }

  // Public URL without SAS, suitable for sharing in email
  const publicUrl = `https://${name}.blob.core.windows.net/${cont}/${blobName}`;
  return publicUrl;
}

// Upload multiple attachments and return an array of public URLs.
export async function uploadAttachmentsToAzure(files) {
  if (!files || files.length === 0) return [];

  const uploads = [];
  for (const f of files) {
    if (f) {
      uploads.push(uploadFileToAzure(f));
    }
  }

  return Promise.all(uploads);
}


